import { useEffect, useMemo, useState, type ReactNode } from "react";
import AtriumIcon from "@/components/atrium/AtriumIcon";
import { useActionEvents, useActionItems, type ActionItem, type ActionSourceType } from "@/hooks/useActionItems";
import { useQueueAction, type QueueActionInput } from "@/hooks/useActionQueue";
import { agoLabel, formatTime } from "@/lib/format";

type FilterKey = "all" | "reply" | "tasks" | "meetings" | "alerts" | "ops" | "knowledge" | "snoozed";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "reply", label: "Needs reply" },
  { key: "tasks", label: "Tasks" },
  { key: "meetings", label: "Meetings" },
  { key: "alerts", label: "Alerts" },
  { key: "ops", label: "Ops" },
  { key: "knowledge", label: "Knowledge" },
  { key: "snoozed", label: "Snoozed" },
];

const SOURCE_LABEL: Record<ActionSourceType, string> = {
  email: "Email",
  task: "Task",
  linear_issue: "Linear",
  meeting: "Meeting",
  alert: "Alert",
  job_run: "Ops",
  snapshot: "Snapshot",
  knowledge: "Knowledge",
};

function itemSourceHref(item: ActionItem) {
  return item.source_url || item.deep_link_url;
}

function openLabel(item: ActionItem, variant: "row" | "drawer" = "row") {
  if (item.source_type === "email") return variant === "drawer" ? "Open source in Gmail" : "Open Gmail";
  if (item.source_type === "task") return "Open task";
  if (item.source_type === "linear_issue") return "Open Linear";
  if (item.source_type === "meeting") return "Open Meet";
  if (item.source_type === "knowledge") return "Open Obsidian";
  if (item.source_type === "job_run" || item.source_type === "snapshot") return "Open Ops";
  return "Open related context";
}

function isNeedsReply(item: ActionItem) {
  return item.source_type === "email"
    && (item.reason.toLowerCase().includes("reply") || item.context?.priority === "needs_reply");
}

function inFilter(item: ActionItem, filter: FilterKey) {
  if (filter === "snoozed") return item.status === "snoozed";
  if (item.status === "snoozed") return false;
  if (filter === "all") return true;
  if (filter === "reply") return isNeedsReply(item);
  if (filter === "tasks") return item.source_type === "task" || item.source_type === "linear_issue";
  if (filter === "meetings") return item.source_type === "meeting";
  if (filter === "alerts") return item.source_type === "alert";
  if (filter === "ops") return item.source_type === "job_run" || item.source_type === "snapshot";
  if (filter === "knowledge") return item.source_type === "knowledge";
  return true;
}

function urgencyColor(item: ActionItem) {
  if (item.urgency === "critical") return "var(--a-bad)";
  if (item.urgency === "high") return "var(--a-warn)";
  if (item.status === "snoozed") return "var(--a-faint)";
  return "var(--a-muted)";
}

function sourceIcon(item: ActionItem) {
  if (item.source_type === "email") return "mail";
  if (item.source_type === "task" || item.source_type === "linear_issue") return "check";
  if (item.source_type === "meeting") return "cal";
  if (item.source_type === "alert") return "bell";
  if (item.source_type === "knowledge") return "book";
  return "bolt";
}

function snoozeUntil(hours: number) {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

function actionPayload(item: ActionItem, actionType: QueueActionInput["actionType"], snoozedUntil?: string) {
  if (actionType === "archive_email") return { message_id: item.source_id };
  if (actionType === "snooze_item") return { snoozed_until: snoozedUntil ?? snoozeUntil(3) };
  if (actionType === "acknowledge_alert") {
    return { task_id: String(item.context?.task_id ?? item.source_id) };
  }
  return {};
}

function ActionButton({
  item,
  actionType,
  children,
  disabled,
}: {
  item: ActionItem;
  actionType: QueueActionInput["actionType"];
  children: ReactNode;
  disabled?: boolean;
}) {
  const queue = useQueueAction();
  return (
    <button
      type="button"
      disabled={disabled || queue.isPending}
      onClick={() => queue.mutateAsync({
        itemId: item.item_id,
        actionType,
        payload: actionPayload(item, actionType),
      })}
      className="rounded-[6px] border px-2.5 py-1 text-[12px] disabled:cursor-not-allowed disabled:opacity-45"
      style={{ borderColor: "var(--a-border)", color: "var(--a-text)", background: "var(--a-panel)" }}
      title={actionType === "mark_handled" ? "Marks this as handled in the cockpit only. It does not complete the source task or email." : undefined}
    >
      {children}
    </button>
  );
}

function SourceLink({ item, variant = "row" }: { item: ActionItem; variant?: "row" | "drawer" }) {
  const href = itemSourceHref(item);
  if (!href) {
    return (
      <span
        className="rounded-[6px] border px-2.5 py-1 text-[12px]"
        style={{ borderColor: "var(--a-border)", color: "var(--a-faint)" }}
      >
        No source link
      </span>
    );
  }
  return (
    <a
      href={href}
      target={href.startsWith("http") || href.startsWith("obsidian:") ? "_blank" : undefined}
      rel="noreferrer"
      className="rounded-[6px] border px-2.5 py-1 text-[12px]"
      style={{ borderColor: "var(--a-border)", color: "var(--a-accent)", background: "var(--a-panel)" }}
    >
      {openLabel(item, variant)}
    </a>
  );
}

function RowActions({ item }: { item: ActionItem }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <SourceLink item={item} />
      {item.source_type === "email" && <ActionButton item={item} actionType="archive_email">Archive email</ActionButton>}
      {item.source_type === "alert" && <ActionButton item={item} actionType="acknowledge_alert">Acknowledge alert</ActionButton>}
      <ActionButton item={item} actionType="snooze_item">Snooze 3h</ActionButton>
      <ActionButton item={item} actionType="mark_handled">Mark cockpit-handled</ActionButton>
    </div>
  );
}

function ActionRow({
  item,
  selected,
  onSelect,
}: {
  item: ActionItem;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className="grid gap-3 border-b px-4 py-3 md:grid-cols-[minmax(0,1fr)_auto]"
      style={{
        borderColor: "var(--a-border)",
        background: selected ? "rgba(109, 123, 255, 0.08)" : "transparent",
      }}
    >
      <button
        type="button"
        onClick={onSelect}
        className="min-w-0 text-left"
        style={{ background: "transparent", color: "var(--a-text)" }}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="a-dot" style={{ background: urgencyColor(item) }} />
          <AtriumIcon name={sourceIcon(item)} size={13} />
          <span className="a-chip">{SOURCE_LABEL[item.source_type]}</span>
          {item.client && <span className="a-chip">{item.client}</span>}
          <span className="mono text-[11px]" style={{ color: "var(--a-faint)" }}>
            P{item.priority}
          </span>
        </div>
        <div className="mt-1.5 truncate text-[14px] font-semibold">{item.title}</div>
        <div className="mt-1 line-clamp-2 text-[12px]" style={{ color: "var(--a-muted)" }}>
          {item.reason}{item.summary ? ` - ${item.summary}` : ""}
        </div>
      </button>
      <RowActions item={item} />
    </div>
  );
}

function ContextDrawer({ item }: { item: ActionItem | undefined }) {
  const events = useActionEvents(item?.item_id);
  if (!item) {
    return (
      <aside
        data-testid="action-context-drawer"
        className="min-h-[520px] border-l p-5"
        style={{ borderColor: "var(--a-border)", background: "var(--a-panel)" }}
      >
        <div className="text-[13px]" style={{ color: "var(--a-muted)" }}>Select an item to see source context.</div>
      </aside>
    );
  }

  return (
    <aside
      data-testid="action-context-drawer"
      className="min-h-[520px] border-l p-5"
      style={{ borderColor: "var(--a-border)", background: "var(--a-panel)" }}
    >
      <div className="flex items-center gap-2">
        <span className="a-dot" style={{ background: urgencyColor(item) }} />
        <span className="a-chip">{SOURCE_LABEL[item.source_type]}</span>
        {item.client && <span className="a-chip">{item.client}</span>}
      </div>
      <h2 className="mt-3 text-[20px] font-semibold">{item.title}</h2>
      <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--a-muted)" }}>
        {item.summary || "No source summary available."}
      </p>

      <div className="mt-4 rounded-[8px] border p-3" style={{ borderColor: "var(--a-border)", background: "var(--a-bg)" }}>
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em]" style={{ color: "var(--a-faint)" }}>
          Why this is here
        </div>
        <div className="mt-1 text-[13px]">{item.reason}</div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <SourceLink item={item} variant="drawer" />
        {item.source_type === "email" && <ActionButton item={item} actionType="archive_email">Archive email</ActionButton>}
        {item.source_type === "alert" && <ActionButton item={item} actionType="acknowledge_alert">Acknowledge alert</ActionButton>}
        <ActionButton item={item} actionType="snooze_item">Snooze 3h</ActionButton>
        <ActionButton item={item} actionType="mark_handled">Mark cockpit-handled</ActionButton>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-3 text-[12px]">
        <div>
          <dt style={{ color: "var(--a-faint)" }}>First seen</dt>
          <dd className="mono mt-0.5">{agoLabel(item.first_seen_at)}</dd>
        </div>
        <div>
          <dt style={{ color: "var(--a-faint)" }}>Last seen</dt>
          <dd className="mono mt-0.5">{agoLabel(item.last_seen_at)}</dd>
        </div>
        <div>
          <dt style={{ color: "var(--a-faint)" }}>Synced</dt>
          <dd className="mono mt-0.5">{formatTime(item.synced_at)}</dd>
        </div>
        <div>
          <dt style={{ color: "var(--a-faint)" }}>Status</dt>
          <dd className="mono mt-0.5">{item.status}</dd>
        </div>
      </dl>

      <div className="mt-5">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em]" style={{ color: "var(--a-faint)" }}>
          Action history
        </div>
        <div className="mt-2 space-y-2">
          {(events.data ?? []).length === 0 && (
            <div className="text-[12px]" style={{ color: "var(--a-muted)" }}>No actions recorded yet.</div>
          )}
          {(events.data ?? []).map((event) => (
            <div key={event.id} className="rounded-[7px] border p-2 text-[12px]" style={{ borderColor: "var(--a-border)" }}>
              <div className="font-medium">{event.action_type}</div>
              <div className="mono mt-0.5" style={{ color: "var(--a-muted)" }}>{event.status} - {agoLabel(event.created_at)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em]" style={{ color: "var(--a-faint)" }}>
          Source context
        </div>
        <pre
          className="mt-2 max-h-[180px] overflow-auto rounded-[7px] border p-3 text-[11px] leading-relaxed"
          style={{ borderColor: "var(--a-border)", background: "var(--a-bg)", color: "var(--a-muted)" }}
        >
          {JSON.stringify(item.context ?? {}, null, 2)}
        </pre>
      </div>
    </aside>
  );
}

export default function AtriumToday() {
  const [filter, setFilter] = useState<FilterKey>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading } = useActionItems();
  const rows = data ?? [];

  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map((f) => [f.key, rows.filter((item) => inFilter(item, f.key)).length]),
  ) as Record<FilterKey, number>, [rows]);

  const visible = useMemo(() => rows.filter((item) => inFilter(item, filter)), [rows, filter]);
  const selected = visible.find((item) => item.item_id === selectedId) ?? visible[0];

  useEffect(() => {
    if (!selectedId && visible[0]) setSelectedId(visible[0].item_id);
  }, [selectedId, visible]);

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b px-5 py-4" style={{ borderColor: "var(--a-border)" }}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[24px] font-semibold">Action Inbox</h1>
            <div className="mt-1 text-[13px]" style={{ color: "var(--a-muted)" }}>
              {counts.all} active item{counts.all === 1 ? "" : "s"} - {counts.reply} need reply - {counts.snoozed} snoozed
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-[12px]">
            <span className="a-chip">{isLoading ? "syncing" : "live from os_action_items"}</span>
            <span className="a-chip">writes via bot queue</span>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => {
                setFilter(f.key);
                setSelectedId(null);
              }}
              className="rounded-[7px] border px-3 py-1.5 text-[12px]"
              style={{
                borderColor: filter === f.key ? "var(--a-accent)" : "var(--a-border)",
                color: filter === f.key ? "var(--a-text)" : "var(--a-muted)",
                background: filter === f.key ? "rgba(109, 123, 255, 0.10)" : "transparent",
              }}
            >
              {f.label} <span className="mono" style={{ color: "var(--a-faint)" }}>{counts[f.key]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid flex-1 grid-cols-1 overflow-hidden xl:grid-cols-[minmax(0,1fr)_380px]">
        <section className="overflow-y-auto">
          {visible.length === 0 && (
            <div className="p-8 text-[13px]" style={{ color: "var(--a-muted)" }}>
              No items in this view. This is an empty state from `os_action_items`, not placeholder data.
            </div>
          )}
          {visible.map((item) => (
            <ActionRow
              key={item.item_id}
              item={item}
              selected={item.item_id === selected?.item_id}
              onSelect={() => setSelectedId(item.item_id)}
            />
          ))}
        </section>
        <ContextDrawer item={selected} />
      </div>
    </div>
  );
}
